import type {
  AbstractPartDefinition,
  Blueprint,
  ProductSettings,
} from "@project/emerge-types";
import pick from "lodash/pick";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { AbstractProductDefinition } from "./AbstractProduct";
import type { Manufacturer } from "./Manufacturer";
import type { OptionDB } from "./OptionDB";
import { useProductStoreContext } from "./ProductStoreContext";

const defaultHiddenPartTypes = [
  "Measurements",
  "Volume",
  "koffer",
  "keyboardcase",
  "haube",
  "truhe",
  "case3d",
  "lcase",
  "singledoor",
  "doubledoor",
];

function withoutHiddenPartType(
  hiddenPartTypes: string[],
  partType: string,
): string[] {
  const next: string[] = [];
  for (const type of hiddenPartTypes) {
    if (type !== partType) {
      next.push(type);
    }
  }
  return next;
}

function getNextHiddenPartTypes(
  hiddenPartTypes: string[],
  partType: string,
  visible: boolean,
) {
  const isCurrentlyHidden = hiddenPartTypes.includes(partType);

  if (visible) {
    return isCurrentlyHidden
      ? withoutHiddenPartType(hiddenPartTypes, partType)
      : hiddenPartTypes;
  }

  return isCurrentlyHidden ? hiddenPartTypes : [...hiddenPartTypes, partType];
}

function getToggledHiddenPartTypes(
  hiddenPartTypes: string[],
  partType: string,
) {
  return hiddenPartTypes.includes(partType)
    ? withoutHiddenPartType(hiddenPartTypes, partType)
    : [...hiddenPartTypes, partType];
}

export enum CameraView {
  FRONT = "front",
  BACK = "back",
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  ISOMETRIC = "isometric",
}

// Default state for the product store
const getDefaultState = <
  ProductType extends string,
  SettingsType extends ProductSettings<ProductType>,
  BlueprintType extends Blueprint,
  PartDefinitionType extends AbstractPartDefinition<string>,
>(): InternalProductState<
  ProductType,
  SettingsType,
  BlueprintType,
  PartDefinitionType
> => ({
  product: null,
  buildVersion: 0, // Increments on each build to trigger part re-renders
  hiddenPartTypes: defaultHiddenPartTypes, // Hide measurements, contents, and wireframes by default
  showEdges: false,
  manufacturer: null,
  optionsDb: null,
  selectedSceneId: null,
  onCameraViewChange: () => Promise.resolve(),
  onCameraFit: () => Promise.resolve(),
  onZoomIn: () => {},
  onZoomOut: () => {},
  onSettingsChange: null,
});

export interface PublicProductApi<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> {
  updateUserOptions: (
    options: object,
    opts?: { coalesce?: boolean },
  ) => Promise<void>;
  loadDefaultProduct: (
    type: string,
    variant?: string,
  ) => Promise<AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > | null>;
  loadProduct: (
    settings: SettingsType,
  ) => Promise<AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > | null>;
  togglePartVisibility: (partType: string) => void;
  setPartVisibility: (partType: string, visible: boolean) => void;
  toggleShowEdges: () => void;
  selectScene: (sceneId: string | null) => void;
  setCameraView: (view: CameraView) => Promise<void>;
  fitCamera: () => Promise<void>;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  resetViewState: () => void;
  initialize: (options: {
    manufacturer: Manufacturer<string, string>;
    optionsDb: OptionDB;
  }) => void;
  /** Set a callback to be notified when settings change (e.g., from drag operations) */
  setOnSettingsChange: (callback: ((options: object) => void) | null) => void;
}

export interface InternalProductApi<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> extends PublicProductApi<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > {
  setProduct: (
    product: AbstractProductDefinition<
      SettingsType,
      BlueprintType,
      PartDefinitionType
    > | null,
  ) => void;
  setManufacturer: (manufacturer: Manufacturer<ProductType, string>) => void;
  setOptionsDb: (optionsDb: OptionDB) => void;
  setOnCameraViewChange: (
    onCameraViewChange: (view: CameraView) => Promise<void>,
  ) => void;
  setOnCameraFit: (onCameraFit: () => Promise<void>) => void;
  setOnZoomIn: (onZoomIn: () => void) => void;
  setOnZoomOut: (onZoomOut: () => void) => void;
}

export interface PublicProductState {
  hiddenPartTypes: string[];
  showEdges: boolean;
  selectedSceneId: string | null;
  /** Increments on each build - used for fine-grained re-renders */
  buildVersion: number;
}

export interface InternalProductState<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> extends PublicProductState {
  product: AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > | null;
  manufacturer: Manufacturer<ProductType, string> | null;
  optionsDb: OptionDB | null;
  onCameraViewChange: (view: CameraView) => Promise<void>;
  onCameraFit: () => Promise<void>;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** Callback invoked when settings change (e.g., from drag operations) */
  onSettingsChange: ((options: object) => void) | null;
}

function createProductStoreState<
  ProductType extends string,
  SettingsType extends ProductSettings<ProductType>,
  BlueprintType extends Blueprint,
  PartDefinitionType extends AbstractPartDefinition<string>,
>(
  set: (
    partial:
      | Partial<
          InternalProductState<
            ProductType,
            SettingsType,
            BlueprintType,
            PartDefinitionType
          > &
            InternalProductApi<
              ProductType,
              SettingsType,
              BlueprintType,
              PartDefinitionType
            >
        >
      | ((
          state: InternalProductState<
            ProductType,
            SettingsType,
            BlueprintType,
            PartDefinitionType
          > &
            InternalProductApi<
              ProductType,
              SettingsType,
              BlueprintType,
              PartDefinitionType
            >,
        ) => Partial<
          InternalProductState<
            ProductType,
            SettingsType,
            BlueprintType,
            PartDefinitionType
          > &
            InternalProductApi<
              ProductType,
              SettingsType,
              BlueprintType,
              PartDefinitionType
            >
        >),
  ) => void,
  get: () => InternalProductState<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > &
    InternalProductApi<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    >,
): InternalProductState<
  ProductType,
  SettingsType,
  BlueprintType,
  PartDefinitionType
> &
  InternalProductApi<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  > {
  type ProductInstance = AbstractProductDefinition<
    SettingsType,
    BlueprintType,
    PartDefinitionType
  >;

  type PendingCoalesce = {
    product: ProductInstance;
    options: object;
    selectionGeneration: number;
  };

  type ProductBuildRollback = {
    restore: () => void;
  };

  const pendingCoalesceByProduct = new Map<ProductInstance, PendingCoalesce>();
  const flushPromiseByProduct = new Map<ProductInstance, Promise<void>>();
  const buildPromiseByProduct = new Map<ProductInstance, Promise<void>>();
  let productSelectionGeneration = 0;

  const clearCoalescedQueue = () => {
    pendingCoalesceByProduct.clear();
  };

  const isCurrentProductSelection = (
    product: ProductInstance,
    selectionGeneration: number,
  ) =>
    get().product === product &&
    productSelectionGeneration === selectionGeneration;

  const setSelectedProduct = (product: ProductInstance | null) => {
    productSelectionGeneration += 1;
    clearCoalescedQueue();
    // biome-ignore lint/suspicious/noExplicitAny: globalthis doesn't need to be typed
    (globalThis as any).product = product;
    set({ product });
  };

  const runExclusiveBuild = (
    product: ProductInstance,
    buildTask: () => Promise<void>,
  ): Promise<void> => {
    const previousBuild = buildPromiseByProduct.get(product);
    const runBuildTask = async () => {
      if (get().product !== product) {
        return;
      }
      await buildTask();
    };
    const nextBuild =
      previousBuild === undefined
        ? runBuildTask()
        : previousBuild
            .catch(() => {
              // Keep later updates moving even if the previous build failed.
            })
            .then(async () => {
              await runBuildTask();
            });

    buildPromiseByProduct.set(product, nextBuild);

    return nextBuild.finally(() => {
      if (buildPromiseByProduct.get(product) === nextBuild) {
        buildPromiseByProduct.delete(product);
      }
    });
  };

  const createProductBuildRollback = (
    targetProduct: ProductInstance,
  ): ProductBuildRollback => {
    if (
      typeof targetProduct.createBuildStateSnapshot === "function" &&
      typeof targetProduct.restoreBuildStateSnapshot === "function"
    ) {
      const snapshot = targetProduct.createBuildStateSnapshot();
      return {
        restore: () => targetProduct.restoreBuildStateSnapshot(snapshot),
      };
    }

    const previousSettings = targetProduct.settings;
    const previousBlueprint = targetProduct.blueprint;
    const previousPartDefinitions = targetProduct.partDefinitions;
    return {
      restore: () => {
        targetProduct.settings = previousSettings;
        targetProduct.blueprint = previousBlueprint;
        targetProduct.partDefinitions = previousPartDefinitions;
      },
    };
  };

  const applyOptionsAndBuild = async (
    targetProduct: ProductInstance,
    options: object,
    selectionGeneration: number,
  ): Promise<void> => {
    if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
      return;
    }

    const rollback = createProductBuildRollback(targetProduct);
    targetProduct.settings = {
      ...targetProduct.settings,
      ...options,
    };

    if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
      rollback.restore();
      return;
    }

    try {
      await targetProduct.build();
    } catch (error) {
      rollback.restore();
      throw error;
    }

    if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
      rollback.restore();
      return;
    }

    // biome-ignore lint/suspicious/noExplicitAny: globalthis doesn't need to be typed
    (globalThis as any).product = targetProduct;

    set((state) => ({ buildVersion: state.buildVersion + 1 }));

    const onSettingsChange = get().onSettingsChange;
    if (onSettingsChange) {
      onSettingsChange(options);
    }

    // Post-build correction: let the product clamp now-invalid settings (e.g. an
    // addable margin pushed outside its drag area by this resize/orientation/
    // count change). Apply the correction and rebuild once so the corrected
    // positions show immediately. The correction is idempotent, so the
    // follow-up build's own normalize returns null and the recursion terminates.
    const normalized =
      typeof targetProduct.normalizeAfterBuild === "function"
        ? targetProduct.normalizeAfterBuild()
        : null;
    if (
      normalized &&
      Object.keys(normalized).length > 0 &&
      isCurrentProductSelection(targetProduct, selectionGeneration)
    ) {
      await applyOptionsAndBuild(
        targetProduct,
        normalized,
        selectionGeneration,
      );
    }
  };

  const flushPendingBuild = (product: ProductInstance): Promise<void> => {
    const activeFlush = flushPromiseByProduct.get(product);
    if (activeFlush !== undefined) {
      return activeFlush;
    }

    const flushPromise = runExclusiveBuild(product, async () => {
      while (true) {
        const pendingCoalesce = pendingCoalesceByProduct.get(product);
        if (!pendingCoalesce) {
          break;
        }

        const {
          product: targetProduct,
          options: patch,
          selectionGeneration,
        } = pendingCoalesce;
        pendingCoalesceByProduct.delete(product);

        if (!isCurrentProductSelection(targetProduct, selectionGeneration)) {
          continue;
        }

        await applyOptionsAndBuild(targetProduct, patch, selectionGeneration);
      }
    }).finally(async () => {
      if (flushPromiseByProduct.get(product) === flushPromise) {
        flushPromiseByProduct.delete(product);
      }
      if (pendingCoalesceByProduct.has(product)) {
        await flushPendingBuild(product);
      }
    });

    flushPromiseByProduct.set(product, flushPromise);
    return flushPromise;
  };

  return {
    ...getDefaultState<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    >(),
    setProduct: (product) => {
      setSelectedProduct(product);
    },
    updateUserOptions: async (options, opts) => {
      const currentProduct = get().product;
      if (!currentProduct) return;
      const selectionGeneration = productSelectionGeneration;

      if (opts?.coalesce) {
        const pendingCoalesce = pendingCoalesceByProduct.get(currentProduct);
        if (pendingCoalesce) {
          pendingCoalesceByProduct.set(currentProduct, {
            product: currentProduct,
            options: { ...pendingCoalesce.options, ...options },
            selectionGeneration,
          });
        } else {
          pendingCoalesceByProduct.set(currentProduct, {
            product: currentProduct,
            options: { ...options },
            selectionGeneration,
          });
        }
        await flushPendingBuild(currentProduct);
        return;
      }

      if (
        flushPromiseByProduct.has(currentProduct) ||
        pendingCoalesceByProduct.has(currentProduct)
      ) {
        await flushPendingBuild(currentProduct);
      }

      await runExclusiveBuild(currentProduct, () =>
        applyOptionsAndBuild(currentProduct, options, selectionGeneration),
      );
    },
    loadDefaultProduct: async (type: string, variant?: string) => {
      const manufacturer = get().manufacturer;
      const optionDb = get().optionsDb;
      if (!manufacturer || !optionDb) {
        throw new Error(
          "Cannot load product: manufacturer or optionsDb not available. Call initialize first.",
        );
      }
      const productDefinition = manufacturer.productDefinition;

      // Get base product settings
      const productInfo = manufacturer.products[type as ProductType];

      if (!productInfo) {
        throw new ProductTypeNotFoundError(type);
      }

      const alternativeCuts = variant === "cover_inside";
      const productSettings = productInfo.getSerializedDefaultVersion(
        optionDb,
        { alternativeCuts },
      ) as SettingsType;

      const product = new productDefinition(
        productSettings,
        optionDb,
      ) as unknown as AbstractProductDefinition<
        SettingsType,
        BlueprintType,
        PartDefinitionType
      >;

      if (!product) throw new ProductTypeNotFoundError(type);
      await product.build();
      setSelectedProduct(product);

      return product;
    },
    loadProduct: async (settings: SettingsType) => {
      const manufacturer = get().manufacturer;
      const optionDb = get().optionsDb;
      if (!manufacturer || !optionDb) {
        throw new Error(
          "Cannot load product: manufacturer or optionsDb not available. Call initialize first.",
        );
      }
      const productDefinition = manufacturer.productDefinition;
      const product = new productDefinition(
        settings,
        optionDb,
      ) as unknown as AbstractProductDefinition<
        SettingsType,
        BlueprintType,
        PartDefinitionType
      >;
      await product.build();
      setSelectedProduct(product);

      return product;
    },
    setManufacturer: (manufacturer) => set({ manufacturer }),
    setOptionsDb: (optionsDb) => set({ optionsDb }),
    togglePartVisibility: (partType) => {
      const hiddenPartTypes = getToggledHiddenPartTypes(
        get().hiddenPartTypes,
        partType,
      );
      set({ hiddenPartTypes });
    },
    setPartVisibility: (partType, visible) => {
      const currentHiddenPartTypes = get().hiddenPartTypes;
      const hiddenPartTypes = getNextHiddenPartTypes(
        currentHiddenPartTypes,
        partType,
        visible,
      );

      if (hiddenPartTypes !== currentHiddenPartTypes) {
        set({ hiddenPartTypes });
      }
    },
    toggleShowEdges: () => {
      set((state) => ({ showEdges: !state.showEdges }));
    },
    selectScene: (sceneId) => {
      set({ selectedSceneId: sceneId });
    },
    resetViewState: () => {
      set({
        hiddenPartTypes: defaultHiddenPartTypes,
        showEdges: false,
        selectedSceneId: null,
      });
    },
    reset: () => {
      productSelectionGeneration += 1;
      clearCoalescedQueue();
      set({
        hiddenPartTypes: defaultHiddenPartTypes,
        showEdges: false,
        selectedSceneId: null,
        product: null,
        manufacturer: null,
        optionsDb: null,
        onCameraViewChange: () => Promise.resolve(),
        onCameraFit: () => Promise.resolve(),
        onSettingsChange: null,
      });
    },
    initialize: ({ manufacturer, optionsDb }) => {
      set({ manufacturer, optionsDb });
    },
    setOnCameraViewChange: (onCameraViewChange) => {
      set({ onCameraViewChange });
    },
    setCameraView: async (view: CameraView) => {
      await get().onCameraViewChange(view);
    },
    setOnCameraFit: (onCameraFit) => {
      set({ onCameraFit });
    },
    fitCamera: async () => {
      await get().onCameraFit();
    },
    setOnZoomIn: (onZoomIn) => {
      set({ onZoomIn });
    },
    zoomIn: () => {
      get().onZoomIn();
    },
    setOnZoomOut: (onZoomOut) => {
      set({ onZoomOut });
    },
    zoomOut: () => {
      get().onZoomOut();
    },
    setOnSettingsChange: (callback) => {
      set({ onSettingsChange: callback });
    },
  };
}

export function createProductStore<
  ProductType extends string = string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
>() {
  return create<
    InternalProductState<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    > &
      InternalProductApi<
        ProductType,
        SettingsType,
        BlueprintType,
        PartDefinitionType
      >
  >()((set, get) =>
    createProductStoreState<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    >(set, get),
  );
}

/**
 * @deprecated Use ProductStoreProvider and useProductStoreContext instead.
 * This default store is kept for backward compatibility only.
 */
export const __useProductStore = createProductStore<
  string,
  ProductSettings<string>,
  Blueprint,
  AbstractPartDefinition<string>
>();

// Custom error for product type not found
export class ProductTypeNotFoundError extends Error {
  constructor(productType: string) {
    super(`Es existiert kein Produkttyp mit der Bezeichnung "${productType}"`);
    this.name = "ProductTypeNotFoundError";
  }
}

/**
 * Hook that selects a subset of part definition properties by ID from the context store.
 * Must be used within a ProductStoreProvider.
 *
 * @example
 * ```tsx
 * const partDef = usePartDefinition({
 *   id: 'part-123',
 *   select: ['position', 'rotation'] as const
 * });
 * ```
 */
export function usePartDefinition<
  PartType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
  const Keys extends readonly (keyof PartType)[] = readonly (keyof PartType)[],
>(options: { id: string; select: Keys }): Pick<PartType, Keys[number]> | null {
  const useProductStore = useProductStoreContext();
  return useProductStore(
    useShallow((state) => {
      // Include buildVersion in selector to trigger re-evaluation on builds
      // This is needed because we no longer change the product reference on every build
      // (which was causing 100ms React reconciliation on each drag frame)
      void state.buildVersion;

      const definition = state.product?.partDefinitions.get(options.id);

      if (!definition) return null;

      // Use the keys array directly with pick
      return pick(definition as PartType, options.select) as Pick<
        PartType,
        Keys[number]
      >;
    }),
  );
}

/**
 * @deprecated Use useProductStoreContext directly instead.
 * Hook to get the typed product store state from context.
 */
export function useTypedProductStore<
  ProductType extends string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
>() {
  const useProductStore = useProductStoreContext<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  >();
  return useProductStore((state) => state);
}
